import React from 'react';
import type { ImageAsset, PortableTextBlock as PortableTextBlockType } from '@sanity/types';
import { bgColorClassname } from '@/utils';
import { LinkInterface } from '@/types';
import { RichText, Link, Heading, Image } from '@/components';

interface ImageWithAlt {
  alt?: string;
  image: {
    asset: ImageAsset;
  };
}

interface Section {
  title: string;
  content: PortableTextBlockType;
  includeCtas: boolean;
  image?: ImageWithAlt;
  ctas?: LinkInterface[];
}

interface InfoListProps {
  backgroundColor?: {
    label: string;
    value: string;
  };
  sections?: Section[];
}

const InfoList: React.FC<InfoListProps> = ({ backgroundColor, sections }) => {
  return (
    <div className={backgroundColor ? bgColorClassname(backgroundColor) : ''}>
      <div className='mx-auto flex max-w-screen-xl flex-col px-5 py-8 md:p-[50px]'>
        <div className='info-list'>
          {sections &&
            sections.length > 0 &&
            sections.map((section, sectionIndex) => (
              <div
                key={sectionIndex}
                className='section pb-6 last:pb-0 md:pb-9'
              >
                <Heading
                  title={section.title}
                  className='mb-4'
                />
                <RichText
                  value={section.content}
                  className='text-base'
                />
                {section?.image && (
                  <div className='mt-5 flex justify-center'>
                    <Image
                      image={section?.image?.image}
                      alt={section?.image?.alt || section.title}
                      className='rounded-xl'
                      width={400}
                    />
                  </div>
                )}
                {section.includeCtas && section.ctas && section.ctas.length > 0 && (
                  <div className='mt-6 flex flex-col items-center justify-center gap-4'>
                    {section.ctas.map((cta, ctaIndex) => {
                      const bgColor = backgroundColor ? backgroundColor.value : '';
                      const isBgColorLight = [
                        '#FFE23D',
                        '#FFD700',
                        '#DAF1E2',
                        '#C9EDF3',
                        '#FC9997',
                        '#ACE4EC',
                      ].includes(bgColor || '');

                      return (
                        <Link
                          key={ctaIndex}
                          {...cta}
                          variant={
                            cta.type === 'primary'
                              ? isBgColorLight
                                ? 'outline'
                                : 'default'
                              : 'linkInline'
                          }
                          size={cta.type === 'primary' ? 'sm' : null}
                          className={cta.type === 'primary' ? 'w-full' : ''}
                        >
                          {cta.label}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};

export default InfoList;
